/**
 * Tipos para el módulo Cupones (Boletas de Pago) — Fase 4.4
 *
 * Documentos relacionados:
 *   - requerimientos.md § M9 (Cupones)
 *   - DECISIONES.md #005 P1 (formato código), #006 (cobranza manual)
 *
 * Código de cupón: ATSA-YYYYMM-CUIT-NNN
 *   - YYYYMM: período (ej: "202605")
 *   - CUIT:   empresa sin guiones (ej: "30711490007")
 *   - NNN:    numero de DDJJ zero-padded (ej: "001")
 *
 * Vencimiento: fecha_emision + DIAS_VENCIMIENTO_CUPON días (configurable en Fase 8)
 */

// ── Estados ───────────────────────────────────────────────────────────────────

export type CuponEstado = 'pendiente' | 'pagado' | 'vencido' | 'anulado';

export function getEstadoCuponLabel(estado: CuponEstado | string): string {
  const labels: Record<string, string> = {
    pendiente: 'Pendiente',
    pagado:    'Pagado',
    vencido:   'Vencido',
    anulado:   'Anulado',
  };
  return labels[estado] ?? estado;
}

export function getEstadoCuponColor(
  estado: CuponEstado | string
): 'default' | 'warning' | 'success' | 'error' | 'info' {
  switch (estado) {
    case 'pendiente': return 'warning';
    case 'pagado':    return 'success';
    case 'vencido':   return 'error';
    case 'anulado':   return 'default';
    default:          return 'default';
  }
}

// ── Entidad ───────────────────────────────────────────────────────────────────

export interface Cupon {
  id: string;
  /** Formato: ATSA-YYYYMM-CUIT-NNN */
  codigo: string;
  empresa_id: string;
  periodo_id: string;
  planilla_id: string | null;
  monto_aporte: number;
  interes: number;
  /** Columna generada: monto_aporte + interes */
  monto_total: number;
  fecha_emision: string;
  fecha_vencimiento: string;
  estado: CuponEstado;
  fecha_pago: string | null;
  /** Contador de reimpresiones */
  reimpresion: number;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
  // Joins opcionales
  empresa?: { id: string; razon_social: string; cuit: string } | null;
  periodo?: { id: string; periodo: string; descripcion: string | null } | null;
  planilla?: { id: string; numero: number } | null;
}

// ── Helper: construir código de cupón ─────────────────────────────────────────

/**
 * Genera el código del cupón a partir de los datos de la DDJJ.
 * Formato: ATSA-YYYYMM-CUIT-NNN
 */
export function buildCodigoCupon(params: {
  periodo: string;      // "2026-05"
  cuit: string;         // "30-71149000-7" o "30711490007"
  numero: number;       // 1, 2, 3...
}): string {
  const yyyymm = params.periodo.replace('-', '');                  // "202605"
  const cuit   = params.cuit.replace(/-/g, '');                    // "30711490007"
  const nnn    = String(params.numero).padStart(3, '0');           // "001"
  return `ATSA-${yyyymm}-${cuit}-${nnn}`;
}
